import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { CheckCircle, User, Users, Shield, ArrowRight, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../utils/api';

const US_STATES = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];

const PROVIDERS = ['Dr. Sarah Chen', 'Dr. Michael Patel', 'Nurse Elena Rodriguez'];

const SUPPORT_ROLES = ['Trainer', 'Nutritionist', 'Case Manager', 'Mental Health Counselor'];

const COHORTS = ['Atlanta Medicaid Pilot', 'Intervention Arm', 'Control Group', 'Corporate Wellness'];

const HEALTH_GOALS = ['Sleep', 'Fitness', 'Stress Management', 'Energy', 'Heart Health', 'Weight Management', 'Nutrition'];

const GENDERS = ['Male', 'Female', 'Non-binary', 'Prefer not to say'];

const DATA_CATEGORIES = [
  { id: 'biometrics', label: 'Biometrics (HR, HRV, SpO₂)' },
  { id: 'mood', label: 'Mood Scores' },
  { id: 'habits', label: 'Habit Tracking' },
  { id: 'notes', label: 'Clinical Notes' },
  { id: 'labs', label: 'Lab Results' },
  { id: 'medications', label: 'Medications' }
];

const AddPatientModalEnhanced = ({ open, onClose, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    gender: '',
    city: '',
    state: '',
    zip_code: '',
    health_goals: [],
    patient_tags: [],
    condition_tags: [],
    assigned_provider: '',
    support_team: [],
    cohort: '',
    send_invite: true,
    data_sharing_categories: ['biometrics', 'mood', 'habits', 'notes'],
    enable_provider_requests: true,
    consent_method: 'email_invite'
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const addTag = (field, value) => {
    if (value && !formData[field].includes(value)) {
      setFormData({ ...formData, [field]: [...formData[field], value] });
    }
  };

  const removeTag = (field, value) => {
    setFormData({ ...formData, [field]: formData[field].filter(v => v !== value) });
  };

  const toggleDataCategory = (categoryId) => {
    const categories = formData.data_sharing_categories;
    if (categories.includes(categoryId)) {
      setFormData({ ...formData, data_sharing_categories: categories.filter(c => c !== categoryId) });
    } else {
      setFormData({ ...formData, data_sharing_categories: [...categories, categoryId] });
    }
  };

  const validateStep = () => {
    if (step === 1) {
      if (!formData.first_name || !formData.last_name || !formData.email) {
        toast.error('Please fill in all required fields');
        return false;
      }
      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        toast.error('Please enter a valid email address');
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep()) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);

    try {
      await api.createPatient(formData);
      toast.success(
        formData.send_invite 
          ? 'Patient created! Invitation sent successfully.' 
          : 'Patient created successfully!'
      );
      onSuccess();
      onClose();
      // Reset form
      setStep(1);
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        date_of_birth: '',
        gender: '',
        city: '',
        state: '',
        zip_code: '',
        health_goals: [],
        patient_tags: [],
        condition_tags: [],
        assigned_provider: '',
        support_team: [],
        cohort: '',
        send_invite: true,
        data_sharing_categories: ['biometrics', 'mood', 'habits', 'notes'],
        enable_provider_requests: true,
        consent_method: 'email_invite'
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create patient');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="add-patient-modal-enhanced">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-3">
            <User className="w-6 h-6" style={{ color: '#8F2FDF' }} />
            <span>Add New Patient - Step {step} of 4</span>
          </DialogTitle>
        </DialogHeader>

        {/* Progress Indicator */}
        <div className="flex items-center justify-between mb-6">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center flex-1">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                  s <= step
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {s < step ? <CheckCircle className="w-6 h-6" /> : s}
              </div>
              {s < 4 && (
                <div
                  className={`flex-1 h-1 mx-2 ${
                    s < step ? 'bg-gradient-to-r from-purple-600 to-pink-600' : 'bg-gray-200'
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Step 1: Basic Information */}
        {step === 1 && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name *</Label>
                <Input
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  data-testid="first-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name *</Label>
                <Input
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  data-testid="last-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                data-testid="email-input"
                placeholder="patient@example.com"
              />
              <p className="text-xs text-gray-500">Must be unique - used for login and invitations</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Mobile Number</Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(555) 123-4567"
                  data-testid="phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="date_of_birth">Date of Birth</Label>
                <Input
                  id="date_of_birth"
                  name="date_of_birth"
                  type="date"
                  value={formData.date_of_birth}
                  onChange={handleChange}
                  data-testid="dob-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="gender">Gender</Label>
              <Select
                value={formData.gender}
                onValueChange={(value) => setFormData({ ...formData, gender: value })}
              >
                <SelectTrigger data-testid="gender-select">
                  <SelectValue placeholder="Select gender" />
                </SelectTrigger>
                <SelectContent>
                  {GENDERS.map((g) => (
                    <SelectItem key={g} value={g}>{g}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  data-testid="city-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">State</Label>
                <Select
                  value={formData.state}
                  onValueChange={(value) => setFormData({ ...formData, state: value })}
                >
                  <SelectTrigger data-testid="state-select">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map((state) => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="zip_code">ZIP Code</Label>
                <Input
                  id="zip_code"
                  name="zip_code"
                  value={formData.zip_code}
                  onChange={handleChange}
                  maxLength={5}
                  data-testid="zip-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Health Goals</Label>
              <div className="flex flex-wrap gap-2">
                {HEALTH_GOALS.map((goal) => (
                  <button
                    key={goal}
                    type="button"
                    onClick={() => 
                      formData.health_goals.includes(goal)
                        ? removeTag('health_goals', goal)
                        : addTag('health_goals', goal)
                    }
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      formData.health_goals.includes(goal)
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {goal}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Patient Tags (optional)</Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.patient_tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="flex items-center space-x-1">
                    <span>{tag}</span>
                    <button onClick={() => removeTag('patient_tags', tag)} className="ml-1">×</button>
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder='e.g., "Pilot Cohort B", "Spanish Materials"'
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addTag('patient_tags', e.target.value);
                      e.target.value = '';
                    }
                  }}
                  data-testid="patient-tags-input"
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Assign Provider / Care Team */}
        {step === 2 && (
          <div className="space-y-6">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-gray-700">
                <strong>Note:</strong> Only providers from your organization will appear. Assigned providers gain access to patient's shared data categories.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="assigned_provider">Assigned Provider</Label>
              <Select
                value={formData.assigned_provider}
                onValueChange={(value) => setFormData({ ...formData, assigned_provider: value })}
              >
                <SelectTrigger data-testid="provider-select">
                  <SelectValue placeholder="Select primary provider" />
                </SelectTrigger>
                <SelectContent>
                  {PROVIDERS.map((provider) => (
                    <SelectItem key={provider} value={provider}>{provider}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Support Team (Multi-select)</Label>
              <div className="grid grid-cols-2 gap-2">
                {SUPPORT_ROLES.map((role) => (
                  <label
                    key={role}
                    className="flex items-center space-x-2 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={formData.support_team.includes(role)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          addTag('support_team', role);
                        } else {
                          removeTag('support_team', role);
                        }
                      }}
                      className="rounded"
                    />
                    <span className="text-sm">{role}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="cohort">Group / Cohort (optional)</Label>
              <Select
                value={formData.cohort}
                onValueChange={(value) => setFormData({ ...formData, cohort: value })}
              >
                <SelectTrigger data-testid="cohort-select">
                  <SelectValue placeholder="Select cohort" />
                </SelectTrigger>
                <SelectContent>
                  {COHORTS.map((cohort) => (
                    <SelectItem key={cohort} value={cohort}>{cohort}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Step 3: Consent & Sharing Setup */}
        {step === 3 && (
          <div className="space-y-6">
            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <p className="text-sm text-gray-700">
                <strong>HIPAA Notice:</strong> No data is shared until patient consents. Providers only see data after share toggle is enabled.
              </p>
            </div>

            <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
              <div>
                <p className="font-semibold">Pre-invite to patient app?</p>
                <p className="text-sm text-gray-600">Sends welcome email with setup instructions</p>
              </div>
              <Switch
                checked={formData.send_invite}
                onCheckedChange={(checked) => setFormData({ ...formData, send_invite: checked })}
                data-testid="send-invite-toggle"
              />
            </div>

            <div className="space-y-3">
              <Label>Initial data categories shared with provider</Label>
              {DATA_CATEGORIES.map((category) => (
                <label
                  key={category.id}
                  className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer"
                >
                  <span className="text-sm">{category.label}</span>
                  <Switch
                    checked={formData.data_sharing_categories.includes(category.id)}
                    onCheckedChange={() => toggleDataCategory(category.id)}
                    data-testid={`data-category-${category.id}`}
                  />
                </label>
              ))}
            </div>

            <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
              <div>
                <p className="font-semibold">Enable provider requests?</p>
                <p className="text-sm text-gray-600">Allow provider to request specific data access in future</p>
              </div>
              <Switch
                checked={formData.enable_provider_requests}
                onCheckedChange={(checked) => setFormData({ ...formData, enable_provider_requests: checked })}
                data-testid="provider-requests-toggle"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="consent_method">Consent Method</Label>
              <Select
                value={formData.consent_method}
                onValueChange={(value) => setFormData({ ...formData, consent_method: value })}
              >
                <SelectTrigger data-testid="consent-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="email_invite">Email Invite</SelectItem>
                  <SelectItem value="manual_in_person">Manual Consent in Person</SelectItem>
                  <SelectItem value="phone_consent">Phone Consent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Step 4: Review & Confirm */}
        {step === 4 && (
          <div className="space-y-6">
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm font-semibold text-green-900 mb-2">Ready to create patient record</p>
              <p className="text-sm text-gray-700">Please review the information below before confirming.</p>
            </div>

            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold mb-2">Basic Information</h3>
                <div className="space-y-1 text-sm">
                  <p><strong>Name:</strong> {formData.first_name} {formData.last_name}</p>
                  <p><strong>Email:</strong> {formData.email}</p>
                  {formData.phone && <p><strong>Phone:</strong> {formData.phone}</p>}
                  {formData.date_of_birth && <p><strong>DOB:</strong> {formData.date_of_birth}</p>}
                  {formData.gender && <p><strong>Gender:</strong> {formData.gender}</p>}
                  {formData.health_goals.length > 0 && (
                    <p><strong>Health Goals:</strong> {formData.health_goals.join(', ')}</p>
                  )}
                </div>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold mb-2">Care Team</h3>
                <div className="space-y-1 text-sm">
                  {formData.assigned_provider && <p><strong>Provider:</strong> {formData.assigned_provider}</p>}
                  {formData.support_team.length > 0 && (
                    <p><strong>Support Team:</strong> {formData.support_team.join(', ')}</p>
                  )}
                  {formData.cohort && <p><strong>Cohort:</strong> {formData.cohort}</p>}
                </div>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold mb-2">Consent & Sharing</h3>
                <div className="space-y-1 text-sm">
                  <p><strong>Send Invite:</strong> {formData.send_invite ? 'Yes' : 'No'}</p>
                  <p><strong>Data Categories:</strong> {formData.data_sharing_categories.length} enabled</p>
                  <p><strong>Provider Requests:</strong> {formData.enable_provider_requests ? 'Enabled' : 'Disabled'}</p>
                  <p><strong>Consent Method:</strong> {formData.consent_method.replace('_', ' ')}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-6 pt-6 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={step === 1 ? onClose : handleBack}
            disabled={loading}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {step === 1 ? 'Cancel' : 'Back'}
          </Button>

          {step < 4 ? (
            <Button
              onClick={handleNext}
              className="rounded-full"
              style={{ background: 'linear-gradient(135deg, #8F2FDF 0%, #CE08B1 100%)' }}
              data-testid="next-btn"
            >
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              className="rounded-full"
              style={{ background: 'linear-gradient(135deg, #8F2FDF 0%, #CE08B1 100%)' }}
              disabled={loading}
              data-testid="create-patient-btn"
            >
              {loading ? 'Creating...' : 'Create Patient'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddPatientModalEnhanced;
